import { TranslocoService } from '@ngneat/transloco';
import type { Stodgrund } from '../../../../core/models/stodgrund.model';
import {
  Andringsmarkering,
  EffektaBudgetAnswer,
  IProjektpart,
} from '../budget.interface';

export type StodgrundFieldNameType = {
  beraknadStodniva: string;
  maxStodniva: string;
  stodgrund: string;
  sfsStodgrund: string;
};

export const StodgrundFieldName = {
  beraknadStodniva: 'beraknadStodniva',
  maxStodniva: 'maxStodniva',
  stodgrund: 'stodgrund',
  sfsStodgrund: 'sfsStodgrund',
} as const satisfies StodgrundFieldNameType;

export type StodgrundField =
  (typeof StodgrundFieldName)[keyof typeof StodgrundFieldName];

// =====================================================
// Changemark functions
// =====================================================

/**
 * Hämtar ändringsmarkeringar (från Effekta) för ett visst fält och worksite.
 * Returnerar `undefined` om data saknas eller fältet inte stöds.
 */
function getChangeMarksForField(params: {
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  worksiteId: string;
  field: StodgrundField;
}): Andringsmarkering[] | undefined {
  const { effektaBudgetAnswer, worksiteId, field } = params;

  const part = findProjektpart({ effektaBudgetAnswer, worksiteId });
  if (!part) {
    return undefined;
  }

  switch (field) {
    case StodgrundFieldName.beraknadStodniva: {
      return part.beraknadStodniva?.andringsmarkering;
    }
    case StodgrundFieldName.maxStodniva: {
      return part.maxStodniva?.andringsmarkering;
    }
    case StodgrundFieldName.stodgrund: {
      return part.stodgrund?.andringsmarkering;
    }
    case StodgrundFieldName.sfsStodgrund: {
      return part.sfsStodgrund?.andringsmarkering;
    }
  }
}

/**
 * Räknar ut "ursprungligt" numeriskt värde för ett fält för en specifik worksite.
 *
 * - I shared view eller kompletteringbegäran försöker den först läsa första ändringsmarkeringen.
 * - Annars faller den tillbaka till ursprungsvärdet från Effekta-payload.
 */
export function resolveOriginalFieldValue(params: {
  worksiteId: string;
  field: StodgrundField;
  isSharedView: boolean;
  isChangeRequestKomplettering?: boolean;
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  projektpartValue?: IProjektpart | undefined;
}): number | undefined {
  const {
    worksiteId,
    field,
    isSharedView,
    isChangeRequestKomplettering,
    effektaBudgetAnswer,
  } = params;

  if (!effektaBudgetAnswer) {
    return undefined;
  }

  if (isSharedView || isChangeRequestKomplettering) {
    const marks =
      params.projektpartValue?.id === worksiteId
        ? params.projektpartValue[field]?.andringsmarkering
        : getChangeMarksForField({ effektaBudgetAnswer, worksiteId, field });

    const prevDecision = marks?.[0];
    const parsedPrevDecision = parsePercentToNumber(prevDecision?.varde);
    if (parsedPrevDecision !== undefined) {
      return parsedPrevDecision;
    }
  }

  const raw = getEffektaRawValueForWorksite({
    effektaBudgetAnswer,
    worksiteId,
    field,
  });

  const result = typeof raw === 'number' ? raw : parsePercentToNumber(raw);

  return result ?? undefined;
}

/**
 * Räknar ut "nuvarande" numeriskt värde för ett fält.
 *
 * - I shared view (Effekta eller intern) används senaste ändringsmarkeringen.
 * - I normal vy utan ändringsmarkeringar hämtas värden från state-mappar för
 *   procentfälten (`beraknadStodniva`, `maxStodniva`).
 */
export function resolveCurrentFieldValue(params: {
  worksiteId: string;
  field: StodgrundField;
  isSharedView: boolean;
  isEffektaSharedView: boolean;
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  beraknadStodniva: Map<string, number>;
  maxStodniva: Map<string, number>;
}): number | undefined {
  const {
    worksiteId,
    field,
    isSharedView,
    isEffektaSharedView,
    effektaBudgetAnswer,
    beraknadStodniva,
    maxStodniva,
  } = params;

  const marks = getChangeMarksForField({
    effektaBudgetAnswer,
    worksiteId,
    field,
  });

  if (isEffektaSharedView || isSharedView) {
    const latest = marks?.[1] ?? marks?.[0];
    return parsePercentToNumber(latest?.varde);
  }

  // Inga ändringsmarkeringar från backend, hämta nuvarande värde från state (som uppdateras vid ändring).
  if (field === StodgrundFieldName.beraknadStodniva) {
    return beraknadStodniva.get(worksiteId);
  }

  if (field === StodgrundFieldName.maxStodniva) {
    return maxStodniva.get(worksiteId);
  }

  return undefined;
}

/** Returnerar true om fältet representeras som procent i UI. */
function isPercentField(field: StodgrundField): boolean {
  return (
    field === StodgrundFieldName.beraknadStodniva ||
    field === StodgrundFieldName.maxStodniva
  );
}

/** Hämtar projektpart (Effekta) för ett worksiteId. */
function findProjektpart(params: {
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  worksiteId: string;
}): EffektaBudgetAnswer['projektparter'][number] | undefined {
  const { effektaBudgetAnswer, worksiteId } = params;
  return effektaBudgetAnswer?.projektparter?.find((p) => p.id === worksiteId);
}

function getTotalBeraknadStodniva(
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined,
): number | undefined {
  return effektaBudgetAnswer?.beraknadStodnivaTotalt
    ? (effektaBudgetAnswer.beraknadStodnivaTotalt.vardeDecimal ??
        effektaBudgetAnswer.beraknadStodnivaTotalt.varde)
    : undefined;
}

export function getOriginalTotalBeraknadStodniva(params: {
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  isSharedView: boolean;
  isChangeRequestKomplettering: boolean;
}): number | undefined {
  const { effektaBudgetAnswer, isSharedView, isChangeRequestKomplettering } =
    params;

  if (!effektaBudgetAnswer) {
    return undefined;
  }

  if (isSharedView || isChangeRequestKomplettering) {
    const marks = effektaBudgetAnswer.beraknadStodnivaTotalt?.andringsmarkering;
    const prevDecision = marks?.[0];
    const parsedPrevDecision = parsePercentToNumber(prevDecision?.varde);
    if (parsedPrevDecision !== undefined) {
      return parsedPrevDecision;
    }
  }

  const raw = getTotalBeraknadStodniva(effektaBudgetAnswer);
  return typeof raw === 'number' ? raw : parsePercentToNumber(raw);
}

// =====================================================
// Tooltip functions
// =====================================================

export type ChangeTooltipTextKey =
  | 'ansokan_budget_tooltip'
  | 'begaran_budget_tooltip'
  | 'budget_tooltip_vinnova_change';

export type ChangeTooltipMeta = {
  text: string;
  tooltipTextKey: ChangeTooltipTextKey;
};

type TooltipRow = {
  label: string;
  value: string;
  dateIso: string;
};

/** Väljer relevanta markeringar (tidigaste + senaste) för tooltip. */
function selectTooltipMarks(
  marks: Andringsmarkering[] | undefined,
): Andringsmarkering[] {
  if (!marks?.length) {
    return [];
  }

  const first = marks[0];
  const last = marks[1] ?? marks[0];
  return last === first ? [first] : [first, last];
}

/** Bygger rader till tooltip: label + formatterat värde + datum (ISO). */
function toTooltipRows(params: {
  marks: Andringsmarkering[] | undefined;
  activeLang: string | undefined;
  valueFormatter?: (rawValue: string) => string;
}): TooltipRow[] {
  const tooltipMarks = selectTooltipMarks(params.marks);
  if (tooltipMarks.length === 0) {
    return [];
  }

  return tooltipMarks
    .map((mark) => {
      const label =
        params.activeLang === 'sv'
          ? (mark.text || '').trim()
          : (mark.textEn || '').trim();
      const rawValue = String(mark.vardeDecimal ?? mark.varde ?? '').trim();
      const value = params.valueFormatter
        ? params.valueFormatter(rawValue)
        : rawValue;
      const dateIso = toIsoDateOnly(mark.datum);

      if (!label && !value) {
        return;
      }

      return { label, value, dateIso };
    })
    .filter((row): row is TooltipRow => row !== undefined);
}

/** Hämtar råvärdet från Effekta för en specifik worksite (utan tolkning/formattering). */
function getEffektaRawValueForWorksite(params: {
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  worksiteId: string;
  field: StodgrundField;
}): number | undefined {
  const { effektaBudgetAnswer, worksiteId, field } = params;

  const part = findProjektpart({ effektaBudgetAnswer, worksiteId });
  if (!part) {
    return undefined;
  }

  switch (field) {
    case StodgrundFieldName.beraknadStodniva: {
      return (
        part.beraknadStodniva?.vardeDecimal ?? part.beraknadStodniva?.varde
      );
    }
    case StodgrundFieldName.maxStodniva: {
      return part.maxStodniva?.vardeDecimal ?? part.maxStodniva?.varde;
    }
    case StodgrundFieldName.stodgrund: {
      return part.stodgrund?.vardeDecimal ?? part.stodgrund?.varde;
    }
    case StodgrundFieldName.sfsStodgrund: {
      return part.sfsStodgrund?.vardeDecimal ?? part.sfsStodgrund?.varde;
    }
  }
}

/** Hämtar föregående värde (som text) från Effekta för en specifik worksite, inkl. procentformattering. */
function getPreviousValueFromEffekta(params: {
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  worksiteId: string;
  field: StodgrundField;
  activeLang: string | undefined;
}): string | undefined {
  const { effektaBudgetAnswer, worksiteId, field, activeLang } = params;

  const raw = getEffektaRawValueForWorksite({
    effektaBudgetAnswer,
    worksiteId,
    field,
  });

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (isPercentField(field)) {
      return formatPercentFromNumber(raw, activeLang);
    }
    return String(raw);
  }

  // För nya projektparter är det tidigare procentvärdet 0%.
  if (effektaBudgetAnswer && isPercentField(field)) {
    return formatPercentFromNumber(0, activeLang);
  }

  return undefined;
}

/**
 * Returnerar tooltip-text + nyckel beroende på vy.
 *
 * - Shared view utan `senastBeslut` => ansökan-tooltip.
 * - Med `senastBeslut` => begäran-tooltip.
 * - Effekta shared view utan beslut => ingen tooltip.
 */
export function resolveChangeTooltipMeta(params: {
  translocoService: TranslocoService;
  isSharedView: boolean;
  senastBeslut: Date | undefined;
  isEffektaSharedView: boolean;
}): ChangeTooltipMeta | undefined {
  const { translocoService, isSharedView, senastBeslut, isEffektaSharedView } =
    params;

  if (isSharedView && !senastBeslut) {
    return {
      text: translocoService.translate('ansokan_budget_tooltip'),
      tooltipTextKey: 'ansokan_budget_tooltip',
    };
  }

  if (senastBeslut) {
    return {
      text: translocoService.translate('begaran_budget_tooltip'),
      tooltipTextKey: 'begaran_budget_tooltip',
    };
  }
  if (isEffektaSharedView) {
    return undefined;
  }

  return {
    text: translocoService.translate('budget_tooltip_vinnova_change'),
    tooltipTextKey: 'budget_tooltip_vinnova_change',
  };
}

/**
 * Bygger tooltip för ändringsmarkering (ChangeMark).
 *
 * - Om ändringsmarkeringar finns: renderar 1–2 rader (tidigaste/senaste).
 * - Om inga finns: visar föregående värde (från Effekta eller angiven "previousValue").
 * - `output: 'description'` ger en text för aria/tooltip-beskrivning.
 */
export function buildChangeMarkTooltipHtml(params: {
  worksiteId: string;
  field: StodgrundField;
  effektaBudgetAnswer: EffektaBudgetAnswer | undefined;
  activeLang: string | undefined;
  output?: 'html' | 'description';
  previousValueLabel?: string;
  previousValueDate?: Date | string;
  previousValue?: string;
  valueFormatter?: (rawValue: string) => string;
}): string | undefined {
  const output = params.output ?? 'html';
  const isTotal = params.worksiteId === 'total';

  const effectiveValueFormatter =
    params.valueFormatter ??
    (isPercentField(params.field)
      ? (rawValue: string) => {
          const parsed = parsePercentToNumber(rawValue);
          return parsed === undefined
            ? rawValue
            : formatPercentFromNumber(parsed, params.activeLang);
        }
      : undefined);

  const marks = isTotal
    ? params.effektaBudgetAnswer?.beraknadStodnivaTotalt?.andringsmarkering
    : getChangeMarksForField({
        effektaBudgetAnswer: params.effektaBudgetAnswer,
        worksiteId: params.worksiteId,
        field: params.field,
      });

  const tooltipRows = toTooltipRows({
    marks,
    activeLang: params.activeLang,
    valueFormatter: effectiveValueFormatter,
  });
  if (tooltipRows.length === 0) {
    let previousValueFromEffekta: string | undefined;

    if (isTotal) {
      const raw = getTotalBeraknadStodniva(params.effektaBudgetAnswer);
      previousValueFromEffekta =
        raw === undefined || raw === null ? undefined : String(raw);
    } else {
      previousValueFromEffekta = getPreviousValueFromEffekta({
        effektaBudgetAnswer: params.effektaBudgetAnswer,
        worksiteId: params.worksiteId,
        field: params.field,
        activeLang: params.activeLang,
      });
    }

    const previousValue = previousValueFromEffekta ?? params.previousValue;
    if (!previousValue) {
      return undefined;
    }

    if (output === 'description') {
      const resolvedValue = params.valueFormatter
        ? params.valueFormatter(previousValue)
        : previousValue;
      const dateIso = toIsoDateOnly(params.previousValueDate);
      return dateIso ? resolvedValue + ' ' + dateIso : resolvedValue;
    }

    return buildPreviousValueRowHtml({
      previousValue,
      previousValueLabel: params.previousValueLabel,
      previousValueDate: params.previousValueDate,
      valueFormatter: effectiveValueFormatter,
    });
  }

  if (output === 'description') {
    return tooltipRows
      .map((row) => {
        const suffix = row.dateIso ? ' ' + row.dateIso : '';
        return row.label + ': ' + row.value + suffix + '.';
      })
      .join(' ');
  }

  return tooltipRows
    .map((row) => {
      const suffix = row.dateIso ? ' ' + row.dateIso : '';
      return (
        '<div class="change-row">' +
        row.label +
        ': ' +
        row.value +
        suffix +
        '</div>'
      );
    })
    .join('');
}

/** Bygger HTML för "föregående värde" när inga ändringsmarkeringar finns. */
function buildPreviousValueRowHtml(params: {
  previousValue: string;
  previousValueLabel?: string;
  previousValueDate?: Date | string;
  valueFormatter?: (rawValue: string) => string;
}): string {
  const label = (params.previousValueLabel ?? '').trim();
  const dateIso = toIsoDateOnly(params.previousValueDate);
  const suffix = dateIso ? ' ' + dateIso : '';

  const resolvedValue = params.valueFormatter
    ? params.valueFormatter(params.previousValue)
    : params.previousValue;

  const content = label ? label + ': ' + resolvedValue : resolvedValue;
  if (!suffix) {
    return '';
  }
  return '<div class="change-row">' + content + suffix + '</div>';
}
// =====================================================
// Formattering
// =====================================================

/** Försöker normalisera ett datum (string/Date) till ISO-datum (YYYY-MM-DD). */
function toIsoDateOnly(input: Date | string | undefined): string | undefined {
  if (!input) {
    return undefined;
  }

  if (typeof input === 'string') {
    const s = input.trim();
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }

    const parsed = new Date(s);
    return Number.isNaN(parsed.getTime())
      ? undefined
      : parsed.toISOString().slice(0, 10);
  }

  if (input instanceof Date && !Number.isNaN(input.getTime())) {
    return input.toISOString().slice(0, 10);
  }

  return undefined;
}

/**
 * Formaterar stödgrund för tooltip.
 *
 * Vissa stödgrund-namn kan hittas via parent-id, andra endast via paragraph-id.
 */
export function resolveStodgrundTooltipValue(params: {
  stodGrundId: string;
  stodgrunder: Stodgrund[] | undefined;
  resolveNameById: (stodgrundId: string) => string | undefined;
}): string {
  const { stodGrundId, stodgrunder, resolveNameById } = params;

  const directName = resolveNameById(stodGrundId);
  if (directName) {
    return directName;
  }

  const parent = stodgrunder?.find((s) =>
    s.paragrafer?.some((p) => p.stodgrundId.toString() === stodGrundId),
  );
  if (parent) {
    return resolveNameById(parent.stodgrundId.toString()) ?? stodGrundId;
  }
  return stodGrundId;
}

/**
 * Försöker tolka ett procentvärde till number.
 *
 * Accepterar t.ex. "12,5%", "12.5 %" och numeriska värden.
 */
export function parsePercentToNumber(value: string): number | undefined {
  if (typeof value === 'string') {
    const normalized = value
      .trim()
      .replaceAll('%', '')
      .replaceAll(/\s+/g, '')
      .replaceAll(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function formatPercentFromNumber(
  value: number,
  activeLang: string | undefined,
): string {
  const locale = activeLang === 'sv' ? 'sv-SE' : 'en-US';
  return new Intl.NumberFormat(locale).format(value) + '%';
}
